import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { Badge, TX_STATUS, fmtDate, money, useTable, SortTh, Pager, selectCls } from "./shared";

const METHOD = { card: "Carta", twint: "TWINT", paypal: "PayPal" };

const RefundButton = ({ tx, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await adminApi.refund(tx.session_id);
      toast.success("Rimborso effettuato");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore rimborso");
    } finally { setBusy(false); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`refund-${tx.session_id}`} disabled={busy}
          className="text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#ef4444] text-[#5b6168] px-2 py-1 inline-flex items-center gap-1">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Rimborsa
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-white border-[#d5dbde] text-[#2B3036]">
        <AlertDialogHeader>
          <AlertDialogTitle>Confermi il rimborso?</AlertDialogTitle>
          <AlertDialogDescription className="text-[#5b6168]">
            {money(tx.amount, tx.currency)} verranno restituiti a {tx.client_email} tramite Stripe. L'operazione non è reversibile.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7] hover:text-[#E0262C]">Annulla</AlertDialogCancel>
          <AlertDialogAction data-testid="refund-confirm" onClick={run} className="bg-[#ef4444] hover:bg-[#dc2626]">Rimborsa</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const TransactionsPanel = () => {
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const load = useCallback(() => adminApi.transactions().then((d) => setData(d.transactions)).catch(() => toast.error("Errore caricamento transazioni")), []);
  useEffect(() => { load(); }, [load]);

  const filterFn = useCallback((t) => (!state || t.state === state) &&
    (!q || `${t.client_email} ${t.client_name} ${t.campaign_url} ${t.campaign_params?.skill || ""} ${t.campaign_params?.area || ""}`.toLowerCase().includes(q.toLowerCase())), [q, state]);
  const table = useTable(data, { filterFn, initialSort: { key: "created_at", dir: "desc" } });
  const sum = (s) => data.filter((t) => t.state === s).reduce((a, t) => a + (t.amount || 0), 0);

  return (
    <div data-testid="admin-transactions" className="space-y-4">
      <div className="grid grid-cols-3 border border-[#d5dbde] divide-x divide-[#d5dbde]">
        {["paid", "pending", "refunded"].map((s) => (
          <div key={s} className="p-5">
            <Badge map={TX_STATUS} value={s} />
            <div data-testid={`tx-total-${s}`} className="font-display text-3xl mt-3">{data.filter((t) => t.state === s).length}
              <span className="text-sm text-[#6b7280] font-mono ml-2">· {sum(s).toFixed(0)}</span></div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <Input data-testid="tx-search" placeholder="Cerca cliente, sito, campagna..." value={q} onChange={(e) => setQ(e.target.value)}
          className="h-10 w-72 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
        <select data-testid="tx-filter-state" value={state} onChange={(e) => setState(e.target.value)} className={selectCls}>
          <option value="">Tutti gli stati</option>
          <option value="paid">Pagato</option>
          <option value="pending">In attesa</option>
          <option value="refunded">Rimborsato</option>
        </select>
      </div>
      <div className="border border-[#d5dbde] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              <SortTh label="Data" k="created_at" table={table} />
              <SortTh label="Cliente" k="client_email" table={table} />
              <th className="px-4 py-3">Campagna</th>
              <SortTh label="Importo" k="amount" table={table} />
              <SortTh label="Metodo" k="method" table={table} />
              <SortTh label="Stato" k="state" table={table} />
              <th className="px-4 py-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((t) => (
              <tr key={t.session_id} data-testid={`tx-row-${t.session_id}`} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
                <td className="px-4 py-3 font-mono text-xs text-[#5b6168]">{fmtDate(t.created_at, true)}</td>
                <td className="px-4 py-3"><div className="text-sm">{t.client_name}</div><div className="font-mono text-xs text-[#6b7280]">{t.client_email}</div></td>
                <td className="px-4 py-3 max-w-[260px]">
                  <div className="font-mono text-xs truncate">{t.campaign_url}</div>
                  {t.campaign_params && <div className="text-xs text-[#6b7280] truncate">{t.campaign_params.skill} · {t.campaign_params.area}</div>}
                </td>
                <td className="px-4 py-3 font-display text-base aurora-text">{money(t.amount, t.currency)}
                  {t.discount_code && <div data-testid={`tx-discount-${t.session_id}`} className="text-[10px] font-mono text-[#34d399]">{t.discount_code} · -{Number(t.discount_amount || 0).toFixed(2)}</div>}
                </td>
                <td className="px-4 py-3 text-xs font-mono">{METHOD[t.method] || t.method}</td>
                <td className="px-4 py-3"><Badge map={TX_STATUS} value={t.state} testid={`tx-state-${t.session_id}`} /></td>
                <td className="px-4 py-3">{t.state === "paid" && <RefundButton tx={t} onDone={load} />}</td>
              </tr>
            ))}
            {!table.rows.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessuna transazione</td></tr>}
          </tbody>
        </table>
        <Pager table={table} testid="tx-pager" />
      </div>
    </div>
  );
};
